import { useTranslation } from "react-i18next";
import { DriverFinancialModel } from "../../models/driverFinancial";
import { Box, Button, Divider, Grid, IconButton, MenuItem, Pagination, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { TEXT_TITLE_COLOR } from "../../constants/colors";
import useRiderBalanceDetailController from "./controller/detail";
import { ArrowBack } from "@mui/icons-material";
import { numberFormat } from "../../utils/nunberFormat";
import { IMAGE_SUBMISSION } from "../../lib/endpoints/driverFinancial";
import { DEFAULT_ROWS_PER_PAGE } from "../../config-global";
import { BalanceModel } from "../../models/balanceModel";
import { convertDateStringToNormalString } from "../../utils/convertDateFormat";
import DoneIcon from '@mui/icons-material/Done';
import OrderDetailView from "./orderDetail";
import ConfirmDialog from "../../components/ConfirmDialog";
import StatusChip from "./components/confirmStatus";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';


interface Props {
    onClose: VoidFunction;
    selectedData: DriverFinancialModel
    onFetchData: Function
}

const RiderBalanceDetailView = ({ onClose, selectedData, onFetchData }: Props) => {
    const { t, i18n } = useTranslation("global");
    const TableHeadValues: string[] = [t("clearanceMoney_page.No"), t("clearanceMoney_page.Image"), t("clearanceMoney_page.Total Payment"), t("clearanceMoney_page.Date"), t("clearanceMoney_page.Confirm Status"), t("clearanceMoney_page.Confirm By"), ""]

    const controller = useRiderBalanceDetailController({ onClose, selectedData, onFetchData })

    return (
        <>
            {
                controller.openViewOrder ? <OrderDetailView onClose={controller.handleCloseViewOrderDetail} selectedData={selectedData} /> : <>
                    <IconButton onClick={onClose}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }} mb={2}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                            <Typography variant="h4" fontWeight='bold' color={TEXT_TITLE_COLOR}>{selectedData?.rider.username} - {selectedData?.rider?.driverNo}</Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ background: '#c4eaff', p: 4, borderRadius: "12px" }}>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">
                                    <span>{t("clearanceMoney_page.Total Money")}</span>
                                </Typography>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">{numberFormat(selectedData?.totalMoney)} {t("Kip")}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ background: '#ffacb6', p: 4, borderRadius: "12px" }}>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">
                                    <span>
                                        {t("clearanceMoney_page.Total OnePay")}
                                    </span>
                                </Typography>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">{numberFormat(controller.totalOnePay)} {t("Kip")}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ background: '#f8f8f8', p: 4, borderRadius: "12px" }}>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">
                                    <span>
                                        {t("clearanceMoney_page.Total Return Order")}
                                    </span>
                                </Typography>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">{numberFormat(controller.totalReturnOrder)} {t("Kip")}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ background: '#c4eaff', p: 4, borderRadius: "12px" }}>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">
                                    <span>{t("clearanceMoney_page.Total Money Clearance")}</span>
                                </Typography>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">{numberFormat(controller.totalMoneyNeedToPay)} {t("Kip")}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ background: '#c1fec6', p: 4, borderRadius: "12px" }}>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">
                                    <span>
                                        {t("clearanceMoney_page.Total Payment")}
                                    </span>
                                </Typography>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">{numberFormat(controller.balanceSubmissionMoney)} {t("Kip")}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ background: '#F1EB90', p: 4, borderRadius: "12px" }}>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">
                                    <span>
                                        {t("clearanceMoney_page.Total Distance Rider")}
                                    </span>
                                </Typography>
                                <Typography variant='h6' color={TEXT_TITLE_COLOR} fontWeight="bold">{numberFormat(controller.distanceRiderGot)} ກິໂລເເມັດ</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
                            <Button variant="outlined" color='secondary' sx={{ textTransform: 'none' }} onClick={controller.handleOpenViewOrderDetail}>
                                {t("clearanceMoney_page.View All Order")}
                            </Button>
                        </Box>
                        <Box sx={{ display: controller.totalBalance === 0 ? 'flex' : 'none', justifyContent: 'right', alignItems: 'center' }}>
                            <Button variant="outlined" sx={{ color: '#000', textTransform: 'none', border: "1px solid #787878" }} onClick={controller.handleOpenConfirmForm}>
                                <DoneIcon />
                                &nbsp;&nbsp;
                                {t("clearanceMoney_page.Calculate Rider Money")}
                            </Button>
                        </Box>
                    </Box>
                    <Box mt={1}>
                        <TableContainer>
                            <Table>
                                <TableHead sx={{ background: '#f7f7f7' }}>
                                    <TableRow>
                                        {
                                            TableHeadValues.map((item: string, ind: number) => <TableCell key={ind} sx={{ borderBottom: 'none' }}>{item}</TableCell>)
                                        }
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        controller?.rows?.map((item: BalanceModel, index: number) => {
                                            const image = IMAGE_SUBMISSION + item?.image?.url
                                            const status = item?.confirmIsStatus
                                            return (
                                                <TableRow key={item?.id}>
                                                    <TableCell align="left" sx={{ borderBottom: 'none' }}>
                                                        {numberFormat(index + 1 + DEFAULT_ROWS_PER_PAGE * (controller.currentPage - 1))}
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: 'none' }}>
                                                        <img src={image} alt="Submission" onClick={() => window.open(image)} style={{ width: 45, height: 45, cursor: 'pointer' }} />
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: 'none' }}>{numberFormat(item?.totalMoney)} {t("Kip")}</TableCell>
                                                    <TableCell sx={{ borderBottom: 'none' }}>{convertDateStringToNormalString(item?.createdAt)}</TableCell>
                                                    <TableCell sx={{ borderBottom: 'none' }}>
                                                        <StatusChip status={status} />
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: 'none' }}>{item?.confirmByName}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            borderBottom: 'none',
                                                            display: (item?.confirmIsStatus === true || controller.totalBalance === 0) ? 'none' : ''
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="large"
                                                            color="inherit"
                                                            onClick={(e: any) => controller.handleOpenMenu(e, item?.id)}
                                                        >
                                                            <MoreHorizIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                            <Pagination
                                color="primary"
                                page={controller.currentPage}
                                onChange={controller.handleChangePage}
                                count={controller.countPagination}
                            />
                        </Box>
                    </Box>
                    <ConfirmDialog
                        open={controller.openConfirm}
                        title={`${t("clearanceMoney_page.Do you want to calculate the amount?")}`}
                        loading={controller.loadingButton}
                        onCancel={controller.handleCloseConfirmForm}
                        onConfirm={() => controller.handleSubmitClear()}
                    />
                    <Popover
                        open={Boolean(controller.openMenu)}
                        anchorEl={controller.openMenu}
                        onClose={controller.handleCloseMenu}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                p: 1,
                                width: 140,
                                '& .MuiMenuItem-root': {
                                    px: 1,
                                    typography: 'body2',
                                    borderRadius: 0.75,
                                },
                            },
                        }}
                    >
                        <MenuItem onClick={controller.handleOpenConfirmSubmission}>
                            <DoneIcon />
                            &nbsp;
                            {t('Confirm')}
                        </MenuItem>
                    </Popover>
                    <ConfirmDialog
                        open={controller.openConfirmSubmission}
                        title={`${t("clearanceMoney_page.Do you want to confirm this Submission")}`}
                        loading={controller.loadingButton}
                        onCancel={controller.handleCloseConfirmSubmission}
                        onConfirm={() => controller.handleConfirmSubmission()}
                    />
                </>
            }

        </>
    )
}
export default RiderBalanceDetailView